import {gql} from '@apollo/client';

import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {areAllAccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import * as ExecuteTasksButton from 'processes/dashboard/ExecuteTasksButton';
import * as ProcessTaskExecutionUtil from 'processes/util/ProcessTaskExecutionUtil';
import APIUtil from 'util/APIUtil';
import {withLoadingScreen} from 'util/Promises';

import {
  ExecuteProcessTasksFromInsightsQuery,
  ExecuteProcessTasksFromInsightsQueryVariables,
} from './__generated/ExecuteProcessTasksFromInsightsQuery';
import buildTakeActionOnColumnOption, {ColumnWithIds} from './buildTakeActionOnColumnOption';

export const EXECUTE_PROCESS_TASKS_FROM_INSIGHTS_QUERY = gql`
  query ExecuteProcessTasksFromInsightsQuery($processTaskIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INSIGHTS) {
    processTasks(ids: $processTaskIds) {
      ...ProcessTaskListingGraphQL
      ...InaccessibleInfoGraphQL
    }
  }

  ${getFragmentWrapper('ProcessTaskListing').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

async function executeTasks({uniqueIds}: ColumnWithIds): Promise<void> {
  const result = await doQuerySafe<
    ExecuteProcessTasksFromInsightsQuery,
    ExecuteProcessTasksFromInsightsQueryVariables
  >({query: EXECUTE_PROCESS_TASKS_FROM_INSIGHTS_QUERY}, {variables: {processTaskIds: uniqueIds}});

  if (!areAllAccessible(result.processTasks)) {
    notifications.error(
      'Cannot execute these tasks because you do not have permissions to execute one or more tasks'
    );
    return;
  }
  const errorMessage = ExecuteTasksButton.getExecuteTooltipMessage(result.processTasks);
  if (errorMessage != null) {
    notifications.error(errorMessage);
    return;
  }

  // We don't wait for this to complete because the execution routine manages its own loading state.
  ProcessTaskExecutionUtil.runProcessTaskExecution(result.processTasks);
}

export default buildTakeActionOnColumnOption({
  getOptionTitle: () => 'Execute workflow task',
  getNoColumnsMessage: () => 'No columns for workflow tasks found',
  icon: 'process-task',
  filterCellContent: (cellContent) => APIUtil.isProcessTask(cellContent),
  handleSelectColumn: (column) => withLoadingScreen(executeTasks(column), 'Executing tasks'),
});
